import React, { useState } from 'react';

import ComboBox, { Props as ComboBoxProps } from 'ds/ComboBox';
import ComboBoxResult from 'ds/ComboBox/ComboBoxResult';
import { AdminListBuilding } from 'ux/Admin/Buildings/types';

export type Props = Omit<ComboBoxProps<number>, 'results' | 'image' | 'onChange' | 'label'> &
  Partial<Pick<ComboBoxProps<number>, 'onChange' | 'label'>> & {
    buildings: AdminListBuilding[];
    selectedBuildingId?: number | null;
    onCreateNew?: VoidFunction;
  };

const BuildingComboBox: React.FC<Props> = ({
  buildings,
  selectedBuildingId,
  label = 'Building',
  onChange,
  onSelect,
  onCreateNew,
  ...props
}) => {
  const building = buildings.find(b => b.id === selectedBuildingId);
  const [buildingQuery, setBuildingQuery] = useState<null | string>(building?.address || null);

  return (
    <ComboBox
      required={false}
      label={label}
      placeholder="Building"
      size="sm"
      results={[
        ...(buildingQuery
          ? buildings.filter(b => b.address.toLowerCase().includes(buildingQuery.toLowerCase()))
          : buildings
        ).map(b => ({
          value: b.id,
          element: (
            <ComboBoxResult
              label={b.address}
              key={b.id}
              query={buildingQuery || ''}
              subLabel={`${b.num_units} units${b.has_stacking_plan === 'true' ? ' • Has stacking plan' : ''}`}
            />
          )
        })),
        ...(onCreateNew
          ? [
              {
                value: -1, // Special value for create new
                element: <ComboBoxResult label="+ Create New Building" key="create-new" query={buildingQuery || ''} />
              }
            ]
          : [])
      ]}
      query={
        selectedBuildingId && buildingQuery === null
          ? buildings.find(b => b.id === selectedBuildingId)?.address || ''
          : buildingQuery || ''
      }
      onChange={params => {
        setBuildingQuery(params.value);
        onChange?.(params);
      }}
      onSelect={value => {
        setBuildingQuery(null);
        if (value === -1 && onCreateNew) {
          onCreateNew();
        } else {
          onSelect(value);
        }
      }}
      showResultsWhenEmpty
      {...props}
    />
  );
};

export default BuildingComboBox;
